// src/context/AppContext.jsx - Updated with Scroll Utilities and IMPROVED Race Condition Handling
import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from 'react';

// Simple storage fallback if service fails
const simpleStorage = {
  save: (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
      return true;
    } catch (error) {
      console.error('Simple storage save failed:', error);
      return false;
    }
  },
  load: (key) => {
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Simple storage load failed:', error);
      return null;
    }
  },
  remove: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Simple storage remove failed:', error);
      return false;
    }
  },
};

// Initial state - REMOVED GAMIFICATION (handled by VibeGamificationContext)
const initialState = {
  // Navigation
  currentView: 'home',
  
  // User progress
  assessmentResults: null,
  assessmentHistory: [], // Store historical assessment results
  completedModules: [],
  completedTools: [],
  userProgress: {
    modules: {},
    tools: {},
    dailyCheckins: [],
    streaks: {
      current: 0,
      longest: 0,
      lastActivity: null,
    },
  },
  
  // App state
  loading: false,
  error: null,
  lastSync: null,
  
  // Preferences
  preferences: {
    theme: 'dark',
    notifications: true,
    soundEffects: true,
    vibeMode: true,
  },
};

// Action types - REMOVED GAMIFICATION ACTIONS
const ActionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_CURRENT_VIEW: 'SET_CURRENT_VIEW',
  
  // Assessment
  COMPLETE_ASSESSMENT: 'COMPLETE_ASSESSMENT',
  UPDATE_ASSESSMENT: 'UPDATE_ASSESSMENT',
  
  // Modules
  COMPLETE_MODULE: 'COMPLETE_MODULE',
  UPDATE_MODULE_PROGRESS: 'UPDATE_MODULE_PROGRESS',
  
  // Tools
  COMPLETE_TOOL: 'COMPLETE_TOOL',
  UPDATE_TOOL_PROGRESS: 'UPDATE_TOOL_PROGRESS',
  
  // Data Management
  LOAD_USER_DATA: 'LOAD_USER_DATA',
  SAVE_USER_DATA: 'SAVE_USER_DATA',
  RESET_USER_DATA: 'RESET_USER_DATA',
  
  // Preferences
  UPDATE_PREFERENCES: 'UPDATE_PREFERENCES',
};

// Reducer - SIMPLIFIED
const appReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return { ...state, loading: action.payload };
      
    case ActionTypes.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
      
    case ActionTypes.SET_CURRENT_VIEW:
      return { ...state, currentView: action.payload };
      
    case ActionTypes.COMPLETE_ASSESSMENT:
      // Add current results to history before updating
      const newHistoryEntry = {
        ...action.payload,
        completedAt: new Date().toISOString(),
        id: Date.now(),
      };
      
      return {
        ...state,
        assessmentResults: action.payload,
        assessmentHistory: [...(Array.isArray(state.assessmentHistory) ? state.assessmentHistory : []), newHistoryEntry],
      };
      
    case ActionTypes.UPDATE_ASSESSMENT:
      return {
        ...state,
        assessmentResults: { ...state.assessmentResults, ...action.payload },
      };
      
    case ActionTypes.COMPLETE_MODULE:
      return {
        ...state,
        completedModules: [...new Set([...state.completedModules, action.payload.moduleId])],
        userProgress: {
          ...state.userProgress,
          modules: {
            ...state.userProgress.modules,
            [action.payload.moduleId]: {
              ...state.userProgress.modules[action.payload.moduleId],
              completed: true,
              completedAt: new Date().toISOString(),
              progressPercentage: 100,
            },
          },
        },
      };
      
    case ActionTypes.UPDATE_MODULE_PROGRESS:
      return {
        ...state,
        userProgress: {
          ...state.userProgress,
          modules: {
            ...state.userProgress.modules,
            [action.payload.moduleId]: {
              ...state.userProgress.modules[action.payload.moduleId],
              ...action.payload.progress,
              lastUpdated: new Date().toISOString(),
            },
          },
        },
      };
      
    case ActionTypes.COMPLETE_TOOL:
      return {
        ...state,
        completedTools: [...new Set([...state.completedTools, action.payload.toolId])],
        userProgress: {
          ...state.userProgress,
          tools: {
            ...state.userProgress.tools,
            [action.payload.toolId]: {
              ...state.userProgress.tools[action.payload.toolId],
              completed: true,
              completedAt: new Date().toISOString(),
              sessionData: action.payload.sessionData,
            },
          },
        },
      };
      
    case ActionTypes.UPDATE_TOOL_PROGRESS:
      return {
        ...state,
        userProgress: {
          ...state.userProgress,
          tools: {
            ...state.userProgress.tools,
            [action.payload.toolId]: {
              ...state.userProgress.tools[action.payload.toolId],
              ...action.payload.progress,
              lastUpdated: new Date().toISOString(),
            },
          },
        },
      };
      
    case ActionTypes.LOAD_USER_DATA:
      console.log('📝 AppContext reducer: Loading user data', action.payload);
      return {
        ...state,
        ...action.payload,
        assessmentHistory: Array.isArray(action.payload?.assessmentHistory) ? action.payload.assessmentHistory : [],
        loading: false,
      };
      
    case ActionTypes.SAVE_USER_DATA:
      return {
        ...state,
        lastSync: new Date().toISOString(),
      };
      
    case ActionTypes.RESET_USER_DATA:
      return {
        ...initialState,
        currentView: 'home',
      };
      
    case ActionTypes.UPDATE_PREFERENCES:
      return {
        ...state,
        preferences: { ...state.preferences, ...action.payload },
      };
      
    default:
      return state;
  }
};

// Create context
const AppContext = createContext();

// Provider component
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Load user data on initialization
  useEffect(() => {
    loadUserData();
  }, []);

  // Memoize saveUserData to prevent infinite loops
  const saveUserDataMemo = useCallback(() => {
    saveUserData();
  }, []); // Empty deps since saveUserData uses state directly

  // Auto-save data when state changes (debounced)
  useEffect(() => {
    // Skip if this is just a lastSync update
    if (state.lastSync && (Date.now() - new Date(state.lastSync).getTime()) < 1000) {
      return;
    }

    const timeoutId = setTimeout(() => {
      saveUserDataMemo();
    }, 2000); // Debounce saves by 2 seconds

    return () => clearTimeout(timeoutId);
  }, [state.assessmentResults, state.assessmentHistory, state.completedModules, state.completedTools, state.userProgress, saveUserDataMemo]);

  // =========== SCROLL UTILITIES ===========
  const scrollToTop = useCallback((behavior = 'smooth') => {
    window.scrollTo({ 
      top: 0, 
      behavior, 
    });
  }, []);

  const scrollToElement = useCallback((elementId, behavior = 'smooth', offset = 0) => {
    const element = document.getElementById(elementId);
    if (element) {
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior,
      });
    }
  }, []);

  const scrollToPosition = useCallback((position, behavior = 'smooth') => {
    window.scrollTo({
      top: position,
      behavior,
    });
  }, []);

  // IMPROVED: Better gamification context access with retry logic
  const getGamificationContext = () => {
    try {
      // Get the context from window (injected by integration hook)
      const context = window.__vibeGamificationContext__;
      if (!context) {
        console.warn('⚠️ Vibe Gamification context not available yet');
        return null;
      }
      
      // Verify the context has the methods we need
      const requiredMethods = ['addXP', 'completeModule', 'updateStreak'];
      const missingMethods = requiredMethods.filter(method => typeof context[method] !== 'function');
      
      if (missingMethods.length > 0) {
        console.warn('⚠️ Gamification context missing methods:', missingMethods);
        return null;
      }
      
      return context;
    } catch (error) {
      console.warn('❌ Error accessing Vibe Gamification context:', error);
      return null;
    }
  };

  // Helper function to get current user ID
const getCurrentUserId = () => {
  try {
    // Try to get user ID from privacy auth context
    const authData = localStorage.getItem('vibe_user_identity');
    if (authData) {
      const parsed = JSON.parse(authData);
      return parsed.userId;
    }
    return null;
  } catch (error) {
    console.warn('Could not get current user ID:', error);
    return null;
  }
};

  // Load all user data from storage
const loadUserData = async () => {
  try {
    dispatch({ type: ActionTypes.SET_LOADING, payload: true });

    const currentUserId = getCurrentUserId();
    const storageKey = currentUserId ? currentUserId : 'guest';
    
    if (!currentUserId) {
      console.log('📥 Loading guest assessment data...');
    } else {
      console.log('📥 Loading data for user:', currentUserId);
    }

    // Try to load from storage service first, fall back to simple storage
    let assessmentResults = null;
    let assessmentHistory = null;
    let progressData = null;
    let preferences = null;

    try {
      // Try complex storage first with user-specific keys
      const { innerVoiceStorage } = await import('../services/storage/InnerVoiceStorageService');
      const { STORAGE_CATEGORIES } = await import('../services/storage/StorageCategories');
      
      assessmentResults = await innerVoiceStorage.load(
        STORAGE_CATEGORIES.ASSESSMENT.BASE,
        `${STORAGE_CATEGORIES.ASSESSMENT.RESULTS}_${storageKey}`,
      );
      
      assessmentHistory = await innerVoiceStorage.load(
        STORAGE_CATEGORIES.ASSESSMENT.BASE,
        `assessment_history_${storageKey}`,
      );
      
      progressData = await innerVoiceStorage.load(
        STORAGE_CATEGORIES.PROGRESS.BASE,
        `${STORAGE_CATEGORIES.PROGRESS.MILESTONES}_${storageKey}`,
      );
      
      preferences = await innerVoiceStorage.getPreferences();
    } catch (storageError) {
      console.warn('Complex storage failed, using simple storage:', storageError);
      
      // Fall back to simple localStorage with user-specific or guest keys
      assessmentResults = simpleStorage.load(`vibe_assessment_results_${storageKey}`);
      assessmentHistory = simpleStorage.load(`vibe_assessment_history_${storageKey}`);
      progressData = simpleStorage.load(`vibe_progress_data_${storageKey}`);
      preferences = simpleStorage.load(`vibe_preferences_${storageKey}`);
    }

    // Combine all loaded data
    const userData = {
      assessmentResults,
      assessmentHistory: Array.isArray(assessmentHistory) ? assessmentHistory : [],
      completedModules: progressData?.completedModules || [],
      completedTools: progressData?.completedTools || [],
      userProgress: progressData?.userProgress || initialState.userProgress,
      preferences: preferences || initialState.preferences,
    };

    console.log('📊 Loaded user data:', {
      assessmentExists: !!userData.assessmentResults,
      completedModules: userData.completedModules.length,
      completedTools: userData.completedTools.length,
    });
    
    console.log('📋 Assessment results loaded:', assessmentResults ? 'Yes ✅' : 'No ❌');
    console.log('📋 Raw assessment data:', assessmentResults);
    console.log('📋 Storage key used:', `vibe_assessment_results_${storageKey}`);

    // Ensure assessment results are properly set in state
    if (assessmentResults) {
      console.log('✅ Setting assessment results in AppContext state');
    } else {
      console.log('❌ No assessment results found to load');
    }

    dispatch({ type: ActionTypes.LOAD_USER_DATA, payload: userData });
  } catch (error) {
    console.error('Error loading user data:', error);
    dispatch({ type: ActionTypes.SET_ERROR, payload: 'Failed to load user data' });
    dispatch({ type: ActionTypes.SET_LOADING, payload: false });
  }
};

  // Save all user data to storage
// ALSO UPDATE: Save user data to use user-specific keys
const saveUserData = async () => {
  try {
    const currentUserId = getCurrentUserId();
    const storageKey = currentUserId ? currentUserId : 'guest';
    
    if (!currentUserId) {
      console.log('💾 Saving guest assessment data...');
    } else {
      console.log('💾 Saving data for user:', currentUserId);
    }

    // Try complex storage first, fall back to simple storage
    try {
      const { innerVoiceStorage } = await import('../services/storage/InnerVoiceStorageService');
      const { STORAGE_CATEGORIES } = await import('../services/storage/StorageCategories');
      
      // Save assessment results with user ID or guest
      if (state.assessmentResults) {
        await innerVoiceStorage.save(
          STORAGE_CATEGORIES.ASSESSMENT.BASE,
          `${STORAGE_CATEGORIES.ASSESSMENT.RESULTS}_${storageKey}`,
          state.assessmentResults,
        );
      }

      // Save assessment history with user ID or guest
      if (state.assessmentHistory && state.assessmentHistory.length > 0) {
        await innerVoiceStorage.save(
          STORAGE_CATEGORIES.ASSESSMENT.BASE,
          `assessment_history_${storageKey}`,
          state.assessmentHistory,
        );
      }

      // Save progress data with user ID or guest
      const progressData = {
        completedModules: state.completedModules,
        completedTools: state.completedTools,
        userProgress: state.userProgress,
        lastUpdated: new Date().toISOString(),
      };

      await innerVoiceStorage.save(
        STORAGE_CATEGORIES.PROGRESS.BASE,
        `${STORAGE_CATEGORIES.PROGRESS.MILESTONES}_${storageKey}`,
        progressData,
      );

      // Save preferences with user ID
      if (innerVoiceStorage.savePreferences) {
        await innerVoiceStorage.savePreferences(state.preferences);
      }
    } catch (storageError) {
      console.warn('Complex storage save failed, using simple storage:', storageError);
      
      // Fall back to simple localStorage with user-specific or guest keys
      if (state.assessmentResults) {
        simpleStorage.save(`vibe_assessment_results_${storageKey}`, state.assessmentResults);
      }
      
      if (state.assessmentHistory && state.assessmentHistory.length > 0) {
        simpleStorage.save(`vibe_assessment_history_${storageKey}`, state.assessmentHistory);
      }
      
      const progressData = {
        completedModules: state.completedModules,
        completedTools: state.completedTools,
        userProgress: state.userProgress,
        lastUpdated: new Date().toISOString(),
      };
      
      simpleStorage.save(`vibe_progress_data_${storageKey}`, progressData);
      simpleStorage.save(`vibe_preferences_${storageKey}`, state.preferences);
    }

    // Remove the dispatch that causes infinite loops
    // dispatch({ type: ActionTypes.SAVE_USER_DATA });
  } catch (error) {
    console.error('Error saving user data:', error);
  }
};

  // IMPROVED: Force sync with better retry logic
  const forceSyncModuleData = () => {
    console.log('🔄 Force syncing module data...');
    
    const maxRetries = 3;
    let retryCount = 0;
    
    const attemptSync = () => {
      const gamification = getGamificationContext();
      
      if (gamification) {
        console.log('✅ Gamification context found, syncing...');
        
        // Sync module progress if available
        if (typeof gamification.syncModuleProgress === 'function') {
          gamification.syncModuleProgress(state.completedModules);
        }
        
        // Ensure all completed modules are registered
        state.completedModules.forEach(moduleId => {
          if (!gamification.completedModules?.includes(moduleId)) {
            console.log('📝 Re-registering module:', moduleId);
            if (typeof gamification.completeModule === 'function') {
              gamification.completeModule(moduleId);
            }
          }
        });
        
        // Refresh gamification data
        if (typeof gamification.refreshGamificationData === 'function') {
          gamification.refreshGamificationData();
        }
        
        console.log('📊 Current state after sync:', {
          completedModules: state.completedModules,
          moduleProgress: state.userProgress.modules,
        });
        
        return true;
      } else {
        retryCount++;
        if (retryCount < maxRetries) {
          console.log(`🔄 Retry ${retryCount}/${maxRetries} - Gamification context not ready, retrying...`);
          setTimeout(attemptSync, 500 * retryCount); // Exponential backoff
        } else {
          console.error('❌ Failed to sync after', maxRetries, 'attempts');
        }
        return false;
      }
    };
    
    // Start the sync attempt
    attemptSync();
    
    // Also force save user data
    saveUserData();
  };

  // Action creators - simplified to prevent infinite re-renders
  const actions = {
    // =========== SCROLL ACTIONS ===========
    scrollToTop,
    scrollToElement,
    scrollToPosition,

    // Navigation
    setCurrentView: useCallback((view) => {
      dispatch({ type: ActionTypes.SET_CURRENT_VIEW, payload: view });
    }, [dispatch]),

    // Assessment
    completeAssessment: async (results) => {
      console.log('💾 AppContext: Completing assessment with results:', results);
      dispatch({ type: ActionTypes.COMPLETE_ASSESSMENT, payload: results });
      
      // Integrate with gamification
      const gamification = getGamificationContext();
      if (gamification) {
        gamification.addXP(100, 'Completed Vibe Check Assessment');
        gamification.updateStreak();
      }
      
      // Force save assessment data immediately
      console.log('💾 AppContext: Force saving assessment data...');
      setTimeout(() => {
        saveUserData();
      }, 100);
    },

    updateAssessment: (updates) => {
      dispatch({ type: ActionTypes.UPDATE_ASSESSMENT, payload: updates });
    },

    // Migrate guest data when user signs up
    migrateGuestData: async () => {
      try {
        console.log('🔄 Migrating guest data to user account...');
        
        // Load guest data
        const guestAssessment = simpleStorage.load('vibe_assessment_results_guest');
        const guestProgress = simpleStorage.load('vibe_progress_data_guest');
        const guestPreferences = simpleStorage.load('vibe_preferences_guest');
        
        if (guestAssessment || guestProgress || guestPreferences) {
          // Update state with guest data
          if (guestAssessment) {
            dispatch({ type: ActionTypes.COMPLETE_ASSESSMENT, payload: guestAssessment });
            console.log('✅ Migrated guest assessment data');
          }
          
          if (guestProgress) {
            if (guestProgress.completedModules) {
              guestProgress.completedModules.forEach(moduleId => {
                dispatch({ type: ActionTypes.COMPLETE_MODULE, payload: { moduleId } });
              });
            }
            if (guestProgress.completedTools) {
              guestProgress.completedTools.forEach(toolId => {
                dispatch({ type: ActionTypes.COMPLETE_TOOL, payload: toolId });
              });
            }
            console.log('✅ Migrated guest progress data');
          }
          
          if (guestPreferences) {
            dispatch({ type: ActionTypes.UPDATE_PREFERENCES, payload: guestPreferences });
            console.log('✅ Migrated guest preferences');
          }
          
          // Save migrated data with user ID
          await saveUserData();
          
          // Clean up guest data
          simpleStorage.remove('vibe_assessment_results_guest');
          simpleStorage.remove('vibe_progress_data_guest');
          simpleStorage.remove('vibe_preferences_guest');
          
          console.log('🧹 Cleaned up guest data');
        } else {
          console.log('📝 No guest data to migrate');
        }
      } catch (error) {
        console.error('❌ Error migrating guest data:', error);
      }
    },

    // IMPROVED: Complete module with better context checking
    completeModule: async (moduleId, progressData = {}) => {
      console.log('🎯 AppContext: Completing module', moduleId);
      
      // First update local state immediately
      dispatch({ 
        type: ActionTypes.COMPLETE_MODULE, 
        payload: { moduleId, progressData }, 
      });
      
      // Then try gamification integration with better retry
      const maxRetries = 3;
      let retryCount = 0;
      
      const tryGamificationIntegration = () => {
        const gamification = getGamificationContext();
        if (gamification) {
          console.log('✅ AppContext: Triggering gamification for module', moduleId);
          
          // Award base module XP first
          gamification.addXP(100, `Completed Module ${moduleId}`);
          
          // Then trigger module completion in gamification
          gamification.completeModule(moduleId);
          
          // Update streak
          if (typeof gamification.updateStreak === 'function') {
            gamification.updateStreak();
          }
          
          return true;
        } else {
          retryCount++;
          if (retryCount < maxRetries) {
            console.log(`🔄 Gamification retry ${retryCount}/${maxRetries}...`);
            setTimeout(tryGamificationIntegration, 300 * retryCount);
          } else {
            console.warn('❌ Gamification context unavailable after', maxRetries, 'attempts');
          }
          return false;
        }
      };

      // Start integration attempt
      tryGamificationIntegration();
      
      // Force save user data
      setTimeout(() => {
        saveUserData();
      }, 300);
    },

    // FIXED: Better module progress tracking
    updateModuleProgress: (moduleId, progress) => {
      console.log('AppContext: Updating module progress', moduleId, progress);
      
      dispatch({
        type: ActionTypes.UPDATE_MODULE_PROGRESS,
        payload: { moduleId, progress },
      });
      
      // Check if module should be marked complete
      if (progress.progressPercentage >= 100 && !state.completedModules.includes(moduleId)) {
        console.log('🏁 Module auto-completing due to 100% progress');
        setTimeout(() => actions.completeModule(moduleId, progress), 100);
      }
      
      // Award incremental XP for progress
      const gamification = getGamificationContext();
      if (gamification && progress.progressPercentage) {
        const xpReward = Math.floor(progress.progressPercentage / 10);
        if (xpReward > 0) {
          gamification.addXP(xpReward, `Module ${moduleId} Progress`);
        }
      }
    },

    // FIXED: Better progress checking
    getModuleProgress: (moduleId) => {
      const moduleProgress = state.userProgress.modules[moduleId] || { 
        progressPercentage: 0, 
        completed: state.completedModules.includes(moduleId), 
      };
      
      // Ensure consistency between completed modules array and progress object
      if (state.completedModules.includes(moduleId) && !moduleProgress.completed) {
        moduleProgress.completed = true;
        moduleProgress.progressPercentage = 100;
      }
      
      return moduleProgress;
    },

    // FIXED: Better completion checking
    isModuleCompleted: (moduleId) => {
      // Check both the completedModules array AND the progress object
      const inCompletedArray = state.completedModules.includes(moduleId);
      const progressObject = state.userProgress.modules[moduleId];
      const progressCompleted = progressObject?.completed === true || progressObject?.progressPercentage >= 100;
      
      return inCompletedArray || progressCompleted;
    },

    // Tools
    completeTool: async (toolId, sessionData = {}) => {
      dispatch({
        type: ActionTypes.COMPLETE_TOOL,
        payload: { toolId, sessionData },
      });
      
      // Integrate with gamification
      const gamification = getGamificationContext();
      if (gamification) {
        gamification.addXP(75, `Completed Tool: ${toolId}`);
        gamification.updateStreak();
      }
    },

    updateToolProgress: (toolId, progress) => {
      dispatch({
        type: ActionTypes.UPDATE_TOOL_PROGRESS,
        payload: { toolId, progress },
      });
    },

    getToolProgress: (toolId) => {
      return state.userProgress.tools[toolId] || {};
    },

    isToolCompleted: (toolId) => {
      return state.completedTools.includes(toolId);
    },

    // Micro-session and exercise completion with gamification integration
    completeMicroSession: async (moduleId, sessionIndex, sessionData = {}) => {
      console.log('🎯 AppContext: Completing micro session', moduleId, sessionIndex);
      
      const currentProgress = actions.getModuleProgress(moduleId);
      const updatedSessions = [...(currentProgress.completedMicroSessions || []), sessionIndex];
      
      await actions.updateModuleProgress(moduleId, {
        ...currentProgress,
        completedMicroSessions: updatedSessions,
        lastUpdated: new Date().toISOString(),
      });
      
      // Award XP through gamification with retry
      const gamification = getGamificationContext();
      if (gamification && typeof gamification.addXP === 'function') {
        const xpReward = sessionData.xp || 25;
        console.log(`💰 Awarding ${xpReward} XP for micro-session`);
        gamification.addXP(xpReward, `Micro-session completed: ${sessionData.title || 'Practice session'}`);
        
        if (typeof gamification.updateStreak === 'function') {
          gamification.updateStreak();
        }
      } else {
        console.warn('⚠️ Could not award XP - gamification context not ready');
      }
    },

    completeExercise: async (moduleId, exerciseIndex, exerciseData = {}) => {
      console.log('🎯 AppContext: Completing exercise', moduleId, exerciseIndex);
      
      const currentProgress = actions.getModuleProgress(moduleId);
      const updatedExercises = [...(currentProgress.completedExercises || []), exerciseIndex];
      
      await actions.updateModuleProgress(moduleId, {
        ...currentProgress,
        completedExercises: updatedExercises,
        lastUpdated: new Date().toISOString(),
      });
      
      // Award XP through gamification
      const gamification = getGamificationContext();
      if (gamification && typeof gamification.addXP === 'function') {
        const xpReward = exerciseData.xp || 50;
        console.log(`💰 Awarding ${xpReward} XP for exercise`);
        gamification.addXP(xpReward, `Exercise completed: ${exerciseData.title || 'Practice exercise'}`);
        
        if (typeof gamification.updateStreak === 'function') {
          gamification.updateStreak();
        }
      } else {
        console.warn('⚠️ Could not award XP - gamification context not ready');
      }
    },

    // Self-talk entries (simplified)
    saveSelfTalkEntry: async (entry) => {
      try {
        const entries = simpleStorage.load('vibe_self_talk_entries') || [];
        const newEntry = {
          ...entry,
          id: Date.now(),
          timestamp: new Date().toISOString(),
        };
        const updatedEntries = [newEntry, ...entries];
        simpleStorage.save('vibe_self_talk_entries', updatedEntries);
        
        // Award XP through gamification
        const gamification = getGamificationContext();
        if (gamification) {
          gamification.addXP(25, 'Self-reflection entry');
          gamification.updateStreak();
        }
        
        return newEntry;
      } catch (error) {
        console.error('Error saving self-talk entry:', error);
        throw error;
      }
    },

    getSelfTalkEntries: async () => {
      try {
        return simpleStorage.load('vibe_self_talk_entries') || [];
      } catch (error) {
        console.error('Error loading self-talk entries:', error);
        return [];
      }
    },

    // Future letters (simplified)
    saveFutureLetterExchange: async (exchange) => {
      try {
        const exchanges = simpleStorage.load('vibe_future_letters') || [];
        const updatedExchange = {
          ...exchange,
          savedAt: new Date().toISOString(),
        };
        const filteredExchanges = exchanges.filter(ex => ex.id !== exchange.id);
        const updatedExchanges = [updatedExchange, ...filteredExchanges];
        simpleStorage.save('vibe_future_letters', updatedExchanges);
        
        // Award XP through gamification
        const gamification = getGamificationContext();
        if (gamification) {
          gamification.addXP(50, 'Future self work');
          gamification.updateStreak();
        }
        
        return updatedExchange;
      } catch (error) {
        console.error('Error saving future letter exchange:', error);
        throw error;
      }
    },

    getFutureLetterExchanges: async () => {
      try {
        return simpleStorage.load('vibe_future_letters') || [];
      } catch (error) {
        console.error('Error loading future letter exchanges:', error);
        return [];
      }
    },

    // IMPROVED: Force sync function
    forceSyncModuleData,

    // Data management
    exportUserData: async () => {
      try {
        return {
          metadata: {
            exportDate: new Date().toISOString(),
            version: '1.0',
            application: 'inner-voice-vibe',
          },
          data: {
            assessmentResults: state.assessmentResults,
            completedModules: state.completedModules,
            completedTools: state.completedTools,
            userProgress: state.userProgress,
            preferences: state.preferences,
          },
        };
      } catch (error) {
        console.error('Error exporting user data:', error);
        throw error;
      }
    },

    resetProgress: async (clearAll = false) => {
      try {
        if (clearAll) {
          // Clear all storage
          const keys = Object.keys(localStorage);
          keys.forEach(key => {
            if (key.startsWith('vibe_') || key.startsWith('inner-voice_')) {
              localStorage.removeItem(key);
            }
          });
        }
        dispatch({ type: ActionTypes.RESET_USER_DATA });
      } catch (error) {
        console.error('Error resetting progress:', error);
        throw error;
      }
    },

    // Preferences
    updatePreferences: (updates) => {
      dispatch({ type: ActionTypes.UPDATE_PREFERENCES, payload: updates });
    },

    getPreferences: () => {
      return state.preferences;
    },

    // Assessment data access
    getAssessmentResults: () => {
      console.log('📋 Getting assessment results from AppContext:', state.assessmentResults);
      return state.assessmentResults;
    },

    hasAssessmentResults: () => {
      const hasResults = !!state.assessmentResults;
      console.log('📋 AppContext has assessment results:', hasResults);
      return hasResults;
    },

    // Storage management
    getStorageStats: () => {
      return {
        totalSize: 'Unknown',
        itemCount: state.completedModules.length,
        categories: {
          'Assessment Data': state.assessmentResults ? 1 : 0,
          'Module Progress': state.completedModules.length,
          'Self-Talk Entries': 0,
          'Future Letters': 0,
        },
        encrypted: false,
        lastBackup: state.lastSync,
      };
    },
  };

  // Context value - simplified to prevent infinite re-renders
  const contextValue = {
    // State
    ...state,
    
    // Actions (including scroll utilities)
    ...actions,
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Hook for using the context
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export default AppContext;